import type { RootState } from '../../../../store'
import type { Dispatch, FormEvent, ReactNode, SetStateAction } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import moment from 'moment/moment'
import { useCallback, useEffect, Fragment, useState } from 'react'
import { MdHighlightOff } from 'react-icons/md'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Select from 'react-select'
// @ts-expect-error //it's okay
import eoc from 'shared_src/episodeofcare'

import draft from '../../../../lib/draft'
import http from '../../../../lib/http'
import { useAppDispatch, useAppSelector } from '../../../../store'
import { encounterSign } from '../../../Encounter/actions'
import { formattedSeconds, getOutcomeOptions, getParticipantOptions, getReasonOptions } from '../../../Encounter/shared'
import { calculateEncounterDuration } from '../../../Encounter/SignEncounter'
import { getLink, igaoPages, igaoPagesOrder } from '../../../igao/utils'
import { Divider } from '../../../shared/Divider'
import { today } from '../../../shared/FormBuilder'
import * as Grid from '../../../shared/grid'
import { Button } from '../../../shared/grid'
import { Checkbox, CircularProgress, DraftButton } from '../../../shared/NarusMaterial'
import { RadioButton } from '../../../shared/NarusMaterial/radio-button'
import { useSidebar } from '../../context'
import * as Sidebar from '../components'
import { SidebarOptionKind } from '../types'

import styles from './encounter-summary.module.css'
import { NewClaimsWarning } from './new-claims-warning'

enum EncountersSummaryEnum {
  'email' = 'email',
  'mobile' = 'mobile',
  'phone' = 'phone',
  'administrative' = 'administrative',
  'cms' = 'cms',
}

type EncountersSummaryTypes = 'email' | 'mobile' | 'phone' | 'administrative' | 'cms'

let presets = [
  {
    label: 'Administrative Computed',
    type: EncountersSummaryEnum.administrative,
    origin: 'inbound',
    urgency: 'non-urgent',
    reason: undefined,
    participant: undefined,
    outcome: undefined,
    time: 'computed'
  },
  {
    label: 'Phone Enrollment Left Voicemail',
    type: EncountersSummaryEnum.phone,
    origin: 'outbound',
    urgency: 'non-urgent',
    reason: 'enrollment',
    participant: 'unsuccessful',
    outcome: 'left-voicemail',
    time: 'computed'
  },
  {
    label: 'Phone Inbound Insurance',
    type: EncountersSummaryEnum.phone,
    origin: 'inbound',
    urgency: 'non-urgent',
    reason: 'insurance',
    participant: 'patient',
    outcome: 'care-coordination',
    time: 'computed'
  },
  {
    label: 'Administrative Non-billable',
    type: EncountersSummaryEnum.administrative,
    origin: 'inbound',
    urgency: 'non-urgent',
    reason: undefined,
    participant: undefined,
    outcome: undefined,
    time: 'non-billable'
  },
  {
    label: 'Administrative Overridden',
    type: EncountersSummaryEnum.administrative,
    origin: 'inbound',
    urgency: 'non-urgent',
    reason: undefined,
    participant: undefined,
    outcome: undefined,
    time: 'override'
  },
  {
    label: 'Urgent Symptoms Inbound',
    type: EncountersSummaryEnum.phone,
    origin: 'inbound',
    reason: 'symptoms-urgent',
    participant: 'patient',
    outcome: 'care-coordination',
    urgency: 'urgent',
    time: 'override'
  },
  {
    label: 'Urgent Symptoms Outbound',
    type: EncountersSummaryEnum.phone,
    origin: 'outbound',
    reason: 'symptom-follow-up',
    participant: 'patient',
    outcome: 'care-coordination',
    urgency: 'urgent',
    time: 'override'
  }
]

interface EncounterTypeProps {
  onChange: (type: EncountersSummaryTypes) => void
  value: EncountersSummaryTypes
}

let selectStyles = {
  control: (styles: any, { isFocused }: {isFocused: boolean}) => ({
    ...styles,
    backgroundColor: '#fff',
    border: '1px solid #DADCE0',
    borderRadius: '4px',
    boxShadow: 'none',
    height: '2.6rem',

    outline: isFocused ? '#5404FF solid 2px' : 'none'
  }),
  container: (styles: any, { isFocused }: {isFocused: boolean}) => ({
    ...styles,
    flex: 1,
    backgroundColor: '#F6F6F6',
    boxShadow: 'none',
    borderRadius: '8px',
    zIndex: isFocused ? 100 : 10
  }),
  indicatorSeparator: (styles: any) => ({
    ...styles,
    marginBottom: 0,
    marginTop: 0
  }),
  dropdownIndicator: (styles: any) => ({
    ...styles,
    padding: '0 18px'
  })
}

export function EncounterType ({ onChange, value }: EncounterTypeProps): JSX.Element {
  function handleChange (value: EncountersSummaryTypes): void {
    onChange(value)
  }

  return (
    <>
      <SectionWrapper>
        <div className={styles.checkboxContainer}>
          <h2>Encounter Type</h2>

          <RadioButton
            className={styles.checkbox}
            options={[
              {
                label: 'Mobile Message',
                value: 'mobile'
              },
              {
                label: 'Email',
                value: 'email'
              },
              {
                label: 'Telephonic',
                value: 'phone'
              },
              {
                label: 'Administrative',
                value: 'administrative'
              }
            ]}
            value={value}
            name='encounter-type'
            onChange={(value) => handleChange(value as EncountersSummaryTypes)}
          />
        </div>
      </SectionWrapper>
      <Divider />
    </>
  )
}

interface CallOriginProps {
  onChange: (type: string) => void
  value: string | null
}

export function CallOrigin ({ onChange, value }: CallOriginProps): JSX.Element {
  function handleChange (value: string): void {
    onChange(value)
  }

  return (
    <>
      <SectionWrapper>
        <div className={styles.checkboxContainer}>
          <h2>Call Origin</h2>

          <RadioButton
            value={value}
            onChange={handleChange}
            options={[{
              value: 'inbound',
              label: 'Inbound'
            }, {
              label: 'Outbound',
              value: 'outbound'
            }]}
            name='call_origin'
            className={styles.checkbox}
          />
        </div>
      </SectionWrapper>
      <Divider />
    </>

  )
}

interface UrgencyProps {
  onChange: (type: string) => void
  value?: string
}

export function Urgency ({ onChange, value }: UrgencyProps): JSX.Element {
  function handleChange (value: string): void {
    onChange(value)
  }

  return (
    <SectionWrapper>
      <div className={styles.checkboxContainer}>
        <h2>Urgency</h2>

        <RadioButton
          value={value ?? null}
          onChange={handleChange}
          options={[{
            label: 'Urgent',
            value: 'urgent'
          }, {
            label: 'Non-urgent',
            value: 'non-urgent'

          }]}
          name='urgency'
          className={styles.checkbox}
        />
      </div>
    </SectionWrapper>
  )
}

function parseDate (dateString: string | undefined): string {
  if (typeof dateString === 'string') {
    return new Date(dateString).toISOString().split('T')?.[0] ?? ''
  }
  return ''
}

interface EncounterTimeProps {
  onChange: Dispatch<SetStateAction<State>>
  state: State
}

export function EncounterTime ({ state, onChange }: EncounterTimeProps): JSX.Element {
  let encounter = useAppSelector((state) => state.encounter?.encounter)
  let [ duration, setDuration ] = useState(0)

  useEffect(() => {
    let interval = setInterval(() => {
      let duration = calculateEncounterDuration(
        encounter?.statusHistory,
        'in-progress'
      )

      setDuration(duration)
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [ encounter?.statusHistory ])

  function handleChange (value: string): void {
    onChange((prev) => ({
      ...prev,
      time: value,
      dateOfServiceOverridden: undefined,
      dateOfServiceOverrideDate: undefined,
      timeHours: undefined,
      timeMinutes: undefined,
      timeDetails: undefined
    }))
  }

  return (
    <SectionWrapper>
      <div
        className={styles.checkboxContainer}
        style={{ paddingBottom: '7px' }}
      >
        <h2>Encounter Time  {formattedSeconds(duration)}</h2>

        <RadioButton
          value={state.time}
          onChange={handleChange}
          options={[{
            label: 'Computed',
            value: 'computed'
          }, {
            label: 'Override',
            value: 'override'
          }, {
            label: 'Non-Billable',
            value: 'non-billable'
          }]}
          name='time-type'
          className={styles.checkbox}
        />

        {state.time === 'override' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '2rem' }}>
            <Grid.Input
              required
              placeholder='Hours'
              onChange={(text) =>
                onChange((prev) => ({
                  ...prev,
                  timeHours: text
                }))}
              value={typeof state?.timeHours === 'string' ? state?.timeHours : ''}
            />
            <Grid.Input
              required
              placeholder='Minutes'
              value={typeof state?.timeMinutes === 'string' ? state?.timeMinutes : ''}
              onChange={(text) =>
                onChange((prev) => ({
                  ...prev,
                  timeMinutes: text
                }))}
            />
            <Grid.Input
              required
              placeholder='Override Comments'
              value={typeof state?.timeDetails === 'string' ? state?.timeDetails : ''}
              onChange={(text) =>
                onChange((prev) => ({
                  ...prev,
                  timeDetails: text
                }))}
            />

            <div style={{ marginLeft: '7px', marginTop: '-10px' }}>
              <Checkbox
                label='Date of Service Override'
                value={state.dateOfServiceOverridden}
                onChange={(v) => onChange((prev) => ({
                  ...prev,
                  dateOfServiceOverridden: v,
                  dateOfServiceOverrideDate: undefined
                }))}
              />
            </div>

            {state.dateOfServiceOverridden && (
              <Grid.Input
                type='date'
                required
                max={today.toISOString().split('T')[0]}
                min={moment().subtract(89, 'days').toISOString().split('T')[0]}
                disabled={!state.dateOfServiceOverridden}
                placeholder='Date of Service'
                value={parseDate(state?.dateOfServiceOverrideDate)}
                onChange={(text) =>
                  onChange((prev) => ({
                    ...prev,
                    dateOfServiceOverrideDate: text
                  }))}
              />
            )}

          </div>
        )}
      </div>
    </SectionWrapper>
  )
}

interface SignatureProps {
  onChange: (type: string) => void
  value: string
}
export function Signature ({ value, onChange }: SignatureProps): JSX.Element {
  return (
    <SectionWrapper>
      <h2>Electronic Signature</h2>
      <div style={{ marginTop: '2rem' }}>
        <Grid.Textarea
          required
          minLength={1}
          placeholder='Narrative'
          value={value}
          rows={5}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </SectionWrapper>
  )
}

interface ReportTypeProps {
  onChange: Dispatch<SetStateAction<State>>
  state: Partial<State>
}

export function ReportType ({ onChange, state }: ReportTypeProps): JSX.Element {
  const reportTypes = [
    {
      label: 'Care Management Summary',
      value: 'Care Management Summary'
    },
    {
      label: 'APS',
      value: 'APS'
    },
    {
      label: 'Research',
      value: 'Research'
    }
  ]

  return (
    <>
      {(state.type === 'cms') && (
        <SectionWrapper>
          <h2>Report Type</h2>
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Select
                options={reportTypes}
                styles={{
                  control: (styles: any) => ({
                    ...styles,
                    backgroundColor: 'white',
                    border: '1px solid #D0D4DB',
                    borderRadius: '6px',
                    boxShadow: 'none'
                  }),
                  container: (styles: any) => ({
                    ...styles,
                    flex: 1,
                    width: '200px',
                    marginLeft: '-8px',
                    backgroundColor: 'white',
                    boxShadow: 'none',
                    borderRadius: '8px'
                  })
                }}
                isMulti={false}
                value={reportTypes?.find((ro) => ro.value === state?.reportType) ?? null}
                placeholder='Care Management Summary'
                onChange={(value) => onChange(prev => ({
                  ...prev,
                  reportType: value?.value ?? undefined
                }))}
              />
            </div>
          </div>
        </SectionWrapper>
      )}
    </>
  )
}

interface ReportQuestionsProps {
  onChange: Dispatch<SetStateAction<State>>
  state: Partial<State>
}

export function ReportQuestions ({ onChange, state }: ReportQuestionsProps): JSX.Element {
  return (
    <>
      {(!state.reportType || state.reportType === 'Care Management Summary') && (
        <SectionWrapper>
          <h2>Discharge Planning</h2>
          <div style={{ marginTop: '2rem' }}>
            <Grid.Textarea
              required
              placeholder='Discharge Planning'
              minLength={1}
              rows={5}
              value={state.dischargeplanning}
              onChange={(event) => onChange((prev) => ({ ...prev, dischargeplanning: event.target.value }))}
            />
          </div>
        </SectionWrapper>
      )}
      {(state.reportType === 'APS') && (
        <SectionWrapper>
          <h2>Present Medical Status</h2>
          <div style={{ marginTop: '2rem' }}>
            <Grid.Textarea
              required
              placeholder='Present Medical Status'
              minLength={1}
              rows={5}
              value={state.presentMedicalStatus}
              onChange={(event) => onChange((prev) => ({ ...prev, presentMedicalStatus: event.target.value }))}
            />
          </div>
          <h2>Care Costs</h2>
          <div style={{ marginTop: '2rem' }}>
            <Grid.Textarea
              required
              placeholder='Care Costs'
              minLength={1}
              rows={5}
              value={state.careCosts}
              onChange={(event) => onChange((prev) => ({ ...prev, careCosts: event.target.value }))}
            />
          </div>
          <h2>Recommendations</h2>
          <div style={{ marginTop: '2rem' }}>
            <Grid.Textarea
              required
              placeholder='Recommendations'
              minLength={1}
              rows={5}
              value={state.recommendations}
              onChange={(event) => onChange((prev) => ({ ...prev, recommendations: event.target.value }))}
            />
          </div>
        </SectionWrapper>
      )}
      {(state.reportType === 'Research') && (
        <SectionWrapper>
          <h2>Question Request</h2>
          <div style={{ marginTop: '2rem' }}>
            <Grid.Textarea
              required
              placeholder='Question Request'
              minLength={1}
              rows={5}
              value={state.questionRequest}
              onChange={(event) => onChange((prev) => ({ ...prev, questionRequest: event.target.value }))}
            />
          </div>
          <h2>Response Findings</h2>
          <div style={{ marginTop: '2rem' }}>
            <Grid.Textarea
              required
              placeholder='Response Findings'
              minLength={1}
              rows={5}
              value={state.responseFindings}
              onChange={(event) => onChange((prev) => ({ ...prev, responseFindings: event.target.value }))}
            />
          </div>
          <h2>Cost Savings</h2>
          <div style={{ marginTop: '2rem' }}>
            <Grid.Textarea
              required
              placeholder='Cost Savings'
              minLength={1}
              rows={5}
              value={state.costSavings}
              onChange={(event) => onChange((prev) => ({ ...prev, costSavings: event.target.value }))}
            />
          </div>
        </SectionWrapper>
      )}
      <Divider />
    </>
  )
}

interface DispositionProps {
  onChange: Dispatch<SetStateAction<State>>
  state: Partial<State>
}

export function DispositionInformation ({ state, onChange }: DispositionProps): JSX.Element {
  let params = useParams()

  let getClientAttributes = (state: RootState): unknown => _.chain(state)
    .get('globalVars.clients', [])
  // @ts-expect-error //it's okay
    .find(c => c.clientName === params.clientName)
    .get('attributes', '')
    .value()

  let { caseStatus, isPatientDead, clientAttributes } = useAppSelector((state) => {
    // @ts-expect-error //it's okay
    let episodesOfCare = state?.patient?.compartment?.EpisodeOfCare ?? null
    // @ts-expect-error //it's okay
    let episodeOfCare = episodesOfCare?.[Object.keys(episodesOfCare)?.[0]]

    return ({
      caseStatus: episodeOfCare?.status,
      isPatientDead: episodeOfCare && eoc.dead(episodeOfCare),
      userManagingOrg: state?.auth?.profile?.app_metadata?.partner_organization,
      clientAttributes: getClientAttributes(state)
    })
  })

  let reasonOptions = null
  let participantOptions = null
  let outcomesOptions = null

  if (state.type === 'phone' || state.type === 'email' || state.type === 'mobile') {
    reasonOptions = getReasonOptions(params.clientName, state.type, state.origin, clientAttributes) as Array<{ value: string, label: string }>
    participantOptions = getParticipantOptions(state.type, caseStatus, isPatientDead, clientAttributes, state.reason) as Array<{ value: string, label: string }>
    outcomesOptions = getOutcomeOptions(state.type, caseStatus, isPatientDead, state.participant, state.reason) as Array<{ value: string, label: string }>
  }

  return (
    <>
      <SectionWrapper>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <h2>Disposition Information</h2>
          <Grid.SelectWrapper
            label='Encounter Reason'
            required
          >
            <Select
              required
              value={reasonOptions?.find((ro) => ro.value === state?.reason) ?? null}
              options={reasonOptions ?? []}
              onChange={(value) => onChange(prev => ({
                ...prev,
                reason: value?.value ?? undefined
              }))}
              styles={selectStyles}
            />
          </Grid.SelectWrapper>
          <Grid.SelectWrapper
            label='Participant'
            required
          >
            <Select
              required
              value={participantOptions?.find((po) => po.value === state?.participant) ?? null}
              options={participantOptions ?? []}
              onChange={(value) => onChange(prev => ({
                ...prev,
                participant: value?.value ?? undefined
              }))}
              styles={selectStyles}
            />
          </Grid.SelectWrapper>
          <Grid.SelectWrapper
            label='Outcome'
            required
          >
            <Select
              required
              value={outcomesOptions?.find((oo) => oo.value === state?.outcome) ?? null}
              options={outcomesOptions ?? []}
              onChange={(value) => onChange(prev => ({
                ...prev,
                outcome: value?.value ?? undefined
              }))}
              styles={selectStyles}
            />
          </Grid.SelectWrapper>

        </div>
      </SectionWrapper>
      <Divider />
    </>
  )
}

interface SelectPresetProps {
  setState: Dispatch<SetStateAction<State>>
  type: string | undefined
}

export function SelectPreset ({ setState, type }: SelectPresetProps): JSX.Element {
  return (
    <SectionWrapper>
      <Grid.SelectWrapper
        label=''
      >
        <div data-test-id='encounter-summary-presets'>
          <Select
            value={null}
            options={presets ?? []}
            placeholder='Select preset'
            onChange={value => {
              value && setState((prev) => ({
                ...prev,
                ...value,
                type: type === 'cms' ? 'cms' : value.type ?? prev.type
              }))
            }}
            styles={{
              ...selectStyles,
              control: (styles: any, { isFocused }: {isFocused: boolean}) => ({
                ...styles,
                backgroundColor: '#fff',
                border: '1px solid #DADCE0',
                borderRadius: '18px',
                boxShadow: 'none',
                height: '2.6rem',

                outline: isFocused ? '#5404FF solid 2px' : 'none'
              }),
              container: (styles: any, { isFocused }: {isFocused: boolean}) => ({
                ...styles,
                flex: 1,
                backgroundColor: '#F6F6F6',
                boxShadow: 'none',
                borderRadius: '18px',
                zIndex: isFocused ? 100 : 10
              })
            }}
          />
        </div>
      </Grid.SelectWrapper>
    </SectionWrapper>
  )
}

interface State {
  type?: EncountersSummaryTypes
  origin: string
  narrative: string
  time: string
  urgency?: string
  reason?: string
  participant?: string
  outcome?: string
  timeHours?: string
  timeMinutes?: string
  timeDetails?: string
  dateOfServiceOverrideDate?: string
  reportType?: string
  dischargeplanning?: string
  presentMedicalStatus?: string
  careCosts?: string
  recommendations?: string
  questionRequest?: string
  responseFindings?: string
  costSavings?: string
  dateOfServiceOverridden?: boolean
}

export interface DraftState {
  getting?: true
  get?: true
  setting?: true
  set?: true
  error?: true
  publishing?: true
}

let initialState = {
  origin: 'inbound',
  narrative: '',
  time: 'computed'
}

interface DraftQueryResponse {
  drafts: Array<{path: string, created: string, updated: string}>
}

// http.getRequest(`/patients/${id}/drafts`)
//     .then(({ drafts }) => {
//       this.setState({ drafts })
//     })

interface UnsavedDraftsProps {
  drafts: DraftQueryResponse['drafts']
}

function UnsavedDrafts ({ drafts }: UnsavedDraftsProps): JSX.Element | null {
  let params = useParams()

  const patientId = params.id
  const clientName = params.clientName

  if (!patientId || !clientName) {
    return null
  }

  return (
    <div
      className='m-[20px] relative pt-4 pb-4 pr-3 pl-3 rounded mt-4 gap-3 flex flex-col'
      style={{ backgroundColor: '#ffe5e5' }}
    >
      <div className='flex flex-row items-center gap-1'>
        <MdHighlightOff
          size='19px'
          className='text-red-600'
        />
        <h2 className='text-black text-smx font-light mt-0 mb-0'>Unsaved Drafts Found</h2>
      </div>

      <div className='flex flex-col gap-2'>
        {/* @ts-expect-error //it's okay */}
        {drafts.sort((a, b) => igaoPagesOrder?.[a.path] - igaoPagesOrder?.[b.path])?.map((draft) => {
          let { name } = [
            ...igaoPages,
            { id: 'current-pregnancy', name: 'Current Pregnancy' },
            { id: 'obstetrical-history', name: 'Obstetrical History' }
          ].find(page => page.id === draft.path) ?? {}
          let link = getLink(patientId, draft.path, clientName)

          if (draft.path === 'behavioral') {
            name = 'Behavioral Health'
            link = `/patient/${clientName}/${patientId}/assessment/standardbehavioral`
          }

          return (
            <Fragment key={draft.path}>
              <Link
                to={link}
                className='text-blue-500'
              >
                {name}
              </Link>
            </Fragment>
          )
        })}
      </div>
      <input
        type='text'
        className='w-[1px] h-[1px] opacity-0 m-0 p-0 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
        required
        onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity('Please publish or discard unsaved drafts')}
      />
    </div>
  )
}

export function EncounterSummary (): JSX.Element {
  let params = useParams()
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let sidebar = useSidebar()

  let { encounter, auth } = useAppSelector((state) => ({
    encounter: state.encounter?.encounter,
    auth: state.auth
  }))

  let [ state, setState ] = useState<State>(initialState)
  let [ draftState, setDraftState ] = useState<DraftState>({ getting: true })

  let supervisor = useAppSelector(state => state?.auth?.profile?.app_metadata?.supervisor)
  let medicalDirector = useAppSelector(state => state?.auth?.profile?.app_metadata?.medical_director)

  let draftQuery = useQuery({
    queryKey: [ 'drafts', params.id ],
    queryFn: () => {
      if (!params.id) {
        return null
      }

      return http.getRequest<DraftQueryResponse>(`/patients/${params.id}/drafts`)
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true
  })

  let { mutate: setDraft } = useMutation({
    mutationFn: async (state: Partial<State>) => {
      let user = localStorage.getItem('asUserId') ? localStorage.getItem('asUserId') : encounter?.author_id ?? ''

      setDraftState({
        setting: true
      })

      return await draft.set({
        resource: params.id,
        path: 'encountersummary',
        user,
        data: state
      })
    },
    onSuccess: () => {
      setDraftState({
        set: true
      })
    },
    onError: () => {
      setDraftState({
        error: true
      })
    }
  })

  let user = localStorage.getItem('asUserId') ? localStorage.getItem('asUserId') : encounter?.author_id ?? ''

  const getDraft = useCallback(async (): Promise<void> => {
    let response = await draft.get({
      path: 'encountersummary',
      resource: params.id,
      user
    })

    setDraftState({
      get: true
    })

    setState((prev) => ({
      ...prev,
      ...(response.data ?? {}),
      type: response?.data?.type ?? state?.type ?? encounter?.document?.type ?? 'patient'
    }))
  }, [ encounter?.document?.type, params.id, state?.type, user ])

  useEffect(() => {
    if (draftState.getting) {
      getDraft()
        .catch(() => {
          console.error('Error while getting draft')
        })
    }
  }, [ draftState.getting, encounter, getDraft, params.id, state.type ])

  useEffect(() => {
    if (_.isEqual(state, initialState)) {
      return
    }
    setDraft(state)
  }, [ setDraft, state ])

  const isSidebarOpen = sidebar.state === SidebarOptionKind.encounter_summary

  useEffect(() => {
    if (isSidebarOpen) {
      getDraft().catch((e) => console.error(e))
    }
  }, [ isSidebarOpen ])

  function handleSubmit (e: FormEvent<HTMLFormElement>): void {
    e.preventDefault()

    let userName = `${auth?.profile?.given_name as string ?? ''} ${auth?.profile?.family_name as string ?? ''}`

    let signingUser = auth?.profile?.sub !== encounter?.author_id ? userName : null

    let data = {
      user: encounter?.author_id,
      type: encounter?.document?.type,
      supervisor,
      medicalDirector,
      ...state
    }

    setDraftState({
      publishing: true
    })

    dispatch(encounterSign(encounter, data, navigate, signingUser))
  }

  if (!encounter || !state.type) {
    return <CircularProgress />
  }

  return (
    <>
      <Sidebar.Header
        link={
          (params.id && params.clientName)
            ? `patient/${params.clientName}/${params.id}/assessment/encountersummary`
            : undefined
        }
        linkTitle='Go to Encounter Summary'
      >
        Encounter Summary
      </Sidebar.Header>
      <Sidebar.Wrapper style={{ padding: '5px 0' }}>
        <form onSubmit={handleSubmit}>
          {/* <SectionWrapper />

          <Divider /> */}

          <div className={styles.encounterSummaryContainer}>
            {encounter?.document?.type !== 'cms' && (
              <SelectPreset
                setState={setState}
                type={encounter?.document?.type}
              />
            )}

            {encounter?.document?.type !== 'cms' && (
              <EncounterType
                onChange={(type) => {
                  setState((prev) => ({
                    ...prev,
                    type,
                    reason: undefined,
                    participant: undefined,
                    outcome: undefined
                  }))
                }}
                value={state.type}
              />
            )}

            {state.type !== 'administrative' && encounter?.document?.type !== 'cms' && (
              <CallOrigin
                value={state?.origin ?? null}
                onChange={(type) => {
                  setState((prev) => ({
                    ...prev,
                    origin: type
                  }))
                }}
              />
            )}

            {state.type !== 'administrative' && encounter?.document?.type !== 'cms' && (
              <DispositionInformation
                state={state}
                onChange={setState}
              />
            )}
            <ReportType
              onChange={setState}
              state={state}
            />
            <Urgency
              value={state?.urgency}
              onChange={(type) => {
                setState((prev) => ({
                  ...prev,
                  urgency: type
                }))
              }}
            />
            <Divider />
            <EncounterTime
              state={state}
              onChange={setState}
            />
            <Divider />

            {state.type === 'cms' && (
              <ReportQuestions
                onChange={setState}
                state={state}
              />
            )}

            <Signature
              onChange={(type) => {
                setState((prev) => ({
                  ...prev,
                  narrative: type
                }))
              }}
              value={state?.narrative ?? ''}
            />
          </div>

          <NewClaimsWarning
            patientId={params.id ?? ''}
            clientName={params.clientName ?? ''}
          />

          {draftQuery?.data?.drafts && draftQuery.data.drafts.length > 0 && (
            <UnsavedDrafts drafts={draftQuery.data.drafts} />
          )}

          <div style={{ marginTop: '3rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem', padding: '0 20px' }}>
            <Button
              type='submit'
              disabled={!!draftState.publishing || draftQuery.isPending}
              style={{
                backgroundColor: '#EAE4FC',
                color: '#6D45F1',
                padding: '.8rem 0',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex'
              }}

            >
              Sign
            </Button>

            <DraftButton draft={draftState} />
          </div>
        </form>
      </Sidebar.Wrapper>
    </>
  )
}

interface Props {
  children: ReactNode
}
export function SectionWrapper ({ children }: Props): JSX.Element {
  return (
    <div style={{ padding: '0 20px' }}>
      {children}
    </div>
  )
}
